// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Checkbox,
  Divider,
  Grid,
  Group,
  Loader,
  MultiSelect,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { Practitioner } from '@medplum/fhirtypes';
import type { ProviderRoleCode } from '@polarissleep/scheduling';
import {
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconGlobe,
  IconInfoCircle,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { FormCard } from '../components/FormCard';
import { useAppointmentTypes } from '../hooks/useAppointmentTypes';
import { useRole } from '../hooks/useRole';
import { TIMEZONE_OPTIONS } from '../pages/settings/constants';
import { getServiceCode, readWeeklyAvailability, withAllWeekdays } from './availability';
import {
  type AvailabilityOverrideDraft,
  V2_DAY_CODES,
  type V2AvailabilityResources,
  type V2DayCode,
  type WeeklyAvailabilityWindow,
} from './types';
import { useV2AvailabilityEditorState } from './useV2AvailabilityEditorState';

interface ProviderAvailabilityV2SectionProps {
  practitioner: Practitioner;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  onSaved?: (practitioner: Practitioner, resources: V2AvailabilityResources) => void;
  allowWeeklyEditing?: boolean;
}

const DAY_LABELS: Record<V2DayCode, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};
const SHORT_DAYS: Record<V2DayCode, string> = {
  sun: 'SUN',
  mon: 'MON',
  tue: 'TUE',
  wed: 'WED',
  thu: 'THU',
  fri: 'FRI',
  sat: 'SAT',
};
const DISPLAY_DAYS: V2DayCode[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const allServicesValue = '__all-services__';
const OVERRIDES_PAGE_SIZE = 5;

export function getAppointmentTypeSelectionValue(
  selectedServiceIds: string[],
  services: V2AvailabilityResources['healthcareServices']
): string[] {
  const serviceIds = services.flatMap((service) => (service.id ? [service.id] : []));
  const selectedIds = new Set(selectedServiceIds);
  const allServicesSelected =
    serviceIds.length > 0 && selectedIds.size === serviceIds.length && serviceIds.every((id) => selectedIds.has(id));
  return allServicesSelected ? [allServicesValue] : selectedServiceIds;
}

export function ProviderAvailabilityV2Section({
  practitioner,
  isEditing,
  onStartEdit,
  onEndEdit,
  onSaved,
  allowWeeklyEditing = true,
}: ProviderAvailabilityV2SectionProps): JSX.Element {
  const { role, isAdmin, loading: roleLoading } = useRole();
  const { appointmentTypes, loading: typesLoading } = useAppointmentTypes();
  const compatibleAppointmentTypes = useMemo(
    () => appointmentTypes.filter((type) => isAdmin || !role || type.providerTypes.includes(role as ProviderRoleCode)),
    [appointmentTypes, isAdmin, role]
  );
  const {
    draft,
    setDraft,
    resetDraft,
    resources,
    loading,
    saving,
    error,
    loadPastOverrides,
    save,
    appointmentConflicts,
    conflictsLoading,
    acknowledgeAppointmentConflicts,
  } = useV2AvailabilityEditorState({
    practitioner,
    appointmentTypes: compatibleAppointmentTypes,
    appointmentTypesLoading: typesLoading,
  });
  const [editingOverride, setEditingOverride] = useState<OverrideForm>();
  const [showWeekendHours, setShowWeekendHours] = useState(false);
  const services = resources?.healthcareServices ?? [];
  const timezoneOptions = useMemo(
    () =>
      draft.timezone && !TIMEZONE_OPTIONS.some((option) => option.value === draft.timezone)
        ? [...TIMEZONE_OPTIONS, { value: draft.timezone, label: draft.timezone }]
        : TIMEZONE_OPTIONS,
    [draft.timezone]
  );
  const form = editingOverride ?? emptyOverrideForm(services[0]?.id);
  const serviceOptions = services.map((service) => ({
    value: service.id ?? '',
    label: getServiceCode(service).display || service.name || service.id || 'Service',
  }));
  const selectedServiceIds = draft.healthcareServiceIds?.length
    ? draft.healthcareServiceIds
    : [draft.healthcareServiceId].filter(Boolean);
  const activeDays = draft.weeklyAvailability.filter((window) => window.startTime && window.endTime);
  const configuredWeekend = activeDays.some((window) => window.dayOfWeek === 'sat' || window.dayOfWeek === 'sun');

  const updateOverride = (update: Partial<OverrideForm>): void => setEditingOverride({ ...form, ...update });
  const updateWindow = (index: number, update: Partial<WeeklyAvailabilityWindow>): void =>
    setDraft({
      ...draft,
      weeklyAvailability: draft.weeklyAvailability.map((window, current) =>
        current === index ? { ...window, ...update } : window
      ),
    });
  const windowsForDay = (day: V2DayCode): Array<{ window: WeeklyAvailabilityWindow; index: number }> =>
    draft.weeklyAvailability.flatMap((window, index) => (window.dayOfWeek === day ? [{ window, index }] : []));
  const setDayEnabled = (day: V2DayCode, enabled: boolean): void => {
    const match = windowsForDay(day)[0];
    if (match) {
      updateWindow(
        match.index,
        enabled
          ? { startTime: match.window.startTime || '09:00', endTime: match.window.endTime || '17:00' }
          : { startTime: '', endTime: '' }
      );
    } else {
      setDraft({
        ...draft,
        weeklyAvailability: [
          ...draft.weeklyAvailability,
          { dayOfWeek: day, startTime: enabled ? '09:00' : '', endTime: enabled ? '17:00' : '' },
        ],
      });
    }
  };
  const clearAll = (): void => setDraft({ ...draft, weeklyAvailability: withAllWeekdays([]) });
  const addWindowForDay = (day: V2DayCode): void =>
    setDraft({
      ...draft,
      weeklyAvailability: [...draft.weeklyAvailability, { dayOfWeek: day, startTime: '09:00', endTime: '17:00' }],
    });
  const copyDayToNext = (day: V2DayCode): void => {
    const source = windowsForDay(day)[0]?.window;
    const next = V2_DAY_CODES[(V2_DAY_CODES.indexOf(day) + 1) % V2_DAY_CODES.length];
    if (source) {
      setDayEnabled(next, true);
    }
    if (source && windowsForDay(next)[0]) {
      updateWindow(windowsForDay(next)[0].index, { startTime: source.startTime, endTime: source.endTime });
    }
  };

  const saveOverrideDraft = (): void => {
    if (!form.startDate || !form.endDate || form.startDate > form.endDate) {
      showNotification({ color: 'red', message: 'Override start date must be on or before its end date.' });
      return;
    }
    if (form.healthcareServiceIds.length === 0) {
      showNotification({ color: 'red', message: 'Select at least one service for this override.' });
      return;
    }
    const retained = form.existingItems.length
      ? draft.overrides.filter((override) => !form.existingItems.includes(override))
      : draft.overrides;
    const expanded = buildOverrideDrafts(form);
    setDraft({ ...draft, overrides: [...retained, ...expanded] });
    setEditingOverride(undefined);
  };
  const handleSave = async (): Promise<void> => {
    try {
      const saved = await save();
      onSaved?.(saved.practitioner, saved);
      onEndEdit();
      showNotification({ color: 'green', icon: <IconCheck size={16} />, message: 'V2 availability saved' });
    } catch {
      showNotification({ color: 'red', message: 'Could not save v2 availability', autoClose: false });
    }
  };
  const handleCancel = (): void => {
    resetDraft();
    setEditingOverride(undefined);
    onEndEdit();
  };
  const action = isEditing ? (
    <Group gap="xs">
      <Badge color="orange" variant="light" radius="xl">
        Unsaved changes
      </Badge>
      <Button variant="default" onClick={handleCancel} disabled={saving}>
        Cancel
      </Button>
      <Button
        onClick={() => handleSave()}
        loading={saving}
        disabled={
          services.length === 0 || conflictsLoading || (appointmentConflicts.length > 0 && !draft.conflictAcknowledged)
        }
      >
        Save
      </Button>
    </Group>
  ) : (
    <Button onClick={onStartEdit} disabled={loading || roleLoading || typesLoading}>
      Edit
    </Button>
  );

  return (
    <FormCard title="Availability" isEditing={isEditing} action={action}>
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <Stack gap="lg">
          <Group gap="xs" c="dimmed">
            <IconGlobe size={17} />
            <Text size="sm">
              {isEditing
                ? `Times are shown in ${timezoneLabel(draft.timezone)}.`
                : `${timezoneLabel(draft.timezone)} · ${weeklyServiceLabel(draft.healthcareServiceId, draft.healthcareServiceIds, services)}`}
            </Text>
          </Group>
          {error && (
            <Alert color="red" styles={{ message: { overflowWrap: 'anywhere' } }}>
              {error}
            </Alert>
          )}
          {services.length === 0 && !error && (
            <Alert color="yellow">
              No projected appointment types are available for this provider. Availability cannot be saved until the v2
              HealthcareService projection is seeded.
            </Alert>
          )}
          {isEditing && allowWeeklyEditing && (
            <>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Select
                  label="Your timezone"
                  data={timezoneOptions}
                  value={draft.timezone}
                  onChange={(timezone) => setDraft({ ...draft, timezone: timezone ?? '' })}
                  searchable
                  comboboxProps={{ withinPortal: false }}
                  required
                />
                <MultiSelect
                  label="These hours are for"
                  data={serviceOptions}
                  value={selectedServiceIds}
                  onChange={(healthcareServiceIds) => {
                    const healthcareServiceId = healthcareServiceIds[0] ?? '';
                    if (
                      healthcareServiceId !== draft.healthcareServiceId ||
                      healthcareServiceIds.length !== selectedServiceIds.length
                    ) {
                      setDraft({
                        ...draft,
                        healthcareServiceId,
                        healthcareServiceIds,
                        overrides: draft.overrides.filter((override) =>
                          healthcareServiceIds.includes(override.healthcareServiceId)
                        ),
                        weeklyAvailability: resources?.schedule
                          ? withAllWeekdays(readWeeklyAvailability(resources.schedule, healthcareServiceId))
                          : withAllWeekdays([]),
                      });
                    }
                  }}
                  searchable
                  clearable
                  comboboxProps={{ withinPortal: false }}
                  required
                />
              </SimpleGrid>
              <Select
                label="Buffer after each appointment"
                data={[
                  { value: '0', label: 'No buffer' },
                  { value: '5', label: '5 min' },
                  { value: '10', label: '10 min' },
                  { value: '15', label: '15 min' },
                  { value: '30', label: '30 min' },
                ]}
                value={String(draft.bufferAfterMinutes ?? 0)}
                onChange={(value) => setDraft({ ...draft, bufferAfterMinutes: value ? Number(value) : 0 })}
                description={`A 30-minute visit at 9:00 blocks you until ${formatBufferEndTime(9, draft.bufferAfterMinutes ?? 0)}.`}
                w={{ base: '100%', sm: '50%' }}
                comboboxProps={{ withinPortal: false }}
              />
            </>
          )}
          <Divider />
          {isEditing && allowWeeklyEditing ? (
            <EditWeeklyHours
              draft={draft}
              windowsForDay={windowsForDay}
              updateWindow={updateWindow}
              setDayEnabled={setDayEnabled}
              addWindowForDay={addWindowForDay}
              copyDayToNext={copyDayToNext}
              clearAll={clearAll}
              showWeekendHours={showWeekendHours || configuredWeekend}
              setShowWeekendHours={setShowWeekendHours}
            />
          ) : (
            <ReadWeeklyHours draft={draft} />
          )}
          <Divider />
          <TimeOffSection
            isEditing={isEditing}
            form={form}
            editingOverride={editingOverride}
            services={services}
            serviceOptions={serviceOptions}
            overrides={draft.overrides}
            timezone={draft.timezone}
            loadPastOverrides={loadPastOverrides}
            updateOverride={updateOverride}
            saveOverrideDraft={saveOverrideDraft}
            setEditingOverride={setEditingOverride}
            setDraft={setDraft}
            draft={draft}
            appointmentConflicts={appointmentConflicts}
            conflictsLoading={conflictsLoading}
            conflictAcknowledged={Boolean(draft.conflictAcknowledged)}
            acknowledgeAppointmentConflicts={acknowledgeAppointmentConflicts}
            removeOverride={(items) =>
              setDraft({ ...draft, overrides: draft.overrides.filter((candidate) => !items.includes(candidate)) })
            }
          />
        </Stack>
      )}
    </FormCard>
  );
}

function EditWeeklyHours({
  draft,
  windowsForDay,
  updateWindow,
  setDayEnabled,
  addWindowForDay,
  copyDayToNext,
  clearAll,
  showWeekendHours,
  setShowWeekendHours,
}: {
  draft: ReturnType<typeof useV2AvailabilityEditorState>['draft'];
  windowsForDay: (day: V2DayCode) => Array<{ window: WeeklyAvailabilityWindow; index: number }>;
  updateWindow: (index: number, update: Partial<WeeklyAvailabilityWindow>) => void;
  setDayEnabled: (day: V2DayCode, enabled: boolean) => void;
  addWindowForDay: (day: V2DayCode) => void;
  copyDayToNext: (day: V2DayCode) => void;
  clearAll: () => void;
  showWeekendHours: boolean;
  setShowWeekendHours: (value: boolean) => void;
}): JSX.Element {
  const days = showWeekendHours ? DISPLAY_DAYS : (['mon', 'tue', 'wed', 'thu', 'fri'] as V2DayCode[]);
  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={700} size="lg">
          Weekly hours
        </Text>
        <Button variant="default" size="sm" onClick={clearAll}>
          Clear all
        </Button>
      </Group>
      <Stack gap={0}>
        {days.map((day) => (
          <DayRow
            key={day}
            day={day}
            windows={windowsForDay(day)}
            updateWindow={updateWindow}
            setDayEnabled={setDayEnabled}
            addWindowForDay={addWindowForDay}
            copyDayToNext={copyDayToNext}
          />
        ))}
      </Stack>
      {!showWeekendHours && (
        <Button
          variant="subtle"
          color="pink"
          size="sm"
          leftSection={<IconPlus size={15} />}
          onClick={() => setShowWeekendHours(true)}
          style={{ alignSelf: 'flex-start' }}
        >
          Add weekend hours
        </Button>
      )}
    </Stack>
  );
}

function DayRow({
  day,
  windows,
  updateWindow,
  setDayEnabled,
  addWindowForDay,
  copyDayToNext,
}: {
  day: V2DayCode;
  windows: Array<{ window: WeeklyAvailabilityWindow; index: number }>;
  updateWindow: (index: number, update: Partial<WeeklyAvailabilityWindow>) => void;
  setDayEnabled: (day: V2DayCode, enabled: boolean) => void;
  addWindowForDay: (day: V2DayCode) => void;
  copyDayToNext: (day: V2DayCode) => void;
}): JSX.Element {
  const first = windows[0];
  const enabled = Boolean(first?.window.startTime && first.window.endTime);
  return (
    <Paper radius={0} p="sm" withBorder={false} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
      <Group wrap="nowrap" gap="md">
        <Switch
          checked={enabled}
          onChange={(event) => setDayEnabled(day, event.currentTarget.checked)}
          aria-label={`${DAY_LABELS[day]} availability`}
          color="pink"
        />
        <Text fw={600} w={140}>
          {DAY_LABELS[day]}
        </Text>
        {enabled && first ? (
          <>
            <TimeSelect
              aria-label={`Start time for ${DAY_LABELS[day]}`}
              value={first.window.startTime}
              onChange={(value) => updateWindow(first.index, { startTime: value ?? '' })}
            />
            <Text c="dimmed">to</Text>
            <TimeSelect
              aria-label={`End time for ${DAY_LABELS[day]}`}
              value={first.window.endTime}
              onChange={(value) => updateWindow(first.index, { endTime: value ?? '' })}
            />
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Add hours for ${DAY_LABELS[day]}`}
              onClick={() => addWindowForDay(day)}
            >
              <IconPlus size={18} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Copy ${DAY_LABELS[day]} hours`}
              onClick={() => copyDayToNext(day)}
            >
              <IconCopy size={17} />
            </ActionIcon>
          </>
        ) : (
          <Text c="dimmed">Unavailable</Text>
        )}
      </Group>
      {windows.slice(1).map((item) => (
        <Group key={item.index} ml={220} mt="xs">
          <TimeSelect
            aria-label={`Start time for ${DAY_LABELS[day]} window ${item.index}`}
            value={item.window.startTime}
            onChange={(value) => updateWindow(item.index, { startTime: value ?? '' })}
          />
          <Text c="dimmed">to</Text>
          <TimeSelect
            aria-label={`End time for ${DAY_LABELS[day]} window ${item.index}`}
            value={item.window.endTime}
            onChange={(value) => updateWindow(item.index, { endTime: value ?? '' })}
          />
          <ActionIcon
            color="red"
            variant="subtle"
            aria-label={`Remove ${DAY_LABELS[day]} window`}
            onClick={() => updateWindow(item.index, { startTime: '', endTime: '' })}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
    </Paper>
  );
}

function TimeSelect({
  value,
  onChange,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (value: string | null) => void;
  'aria-label': string;
}): JSX.Element {
  return (
    <Select
      aria-label={ariaLabel}
      data={timeOptions()}
      value={value || null}
      onChange={onChange}
      w={118}
      comboboxProps={{ withinPortal: false }}
    />
  );
}

function ReadWeeklyHours({ draft }: { draft: ReturnType<typeof useV2AvailabilityEditorState>['draft'] }): JSX.Element {
  return (
    <Stack gap="sm">
      <Text fw={700} size="lg">
        Weekly hours
      </Text>
      <SimpleGrid cols={{ base: 2, xs: 4, md: 7 }} spacing="xs">
        {DISPLAY_DAYS.map((day) => {
          const windows = draft.weeklyAvailability.filter(
            (candidate) => candidate.dayOfWeek === day && candidate.startTime && candidate.endTime
          );
          const hasAvailability = windows.length > 0;
          return (
            <Paper
              key={day}
              p="md"
              ta="center"
              radius="md"
              withBorder
              style={{
                background: hasAvailability ? 'var(--mantine-color-pink-0)' : 'var(--mantine-color-gray-0)',
                borderColor: hasAvailability ? 'var(--mantine-color-pink-2)' : undefined,
              }}
            >
              <Text size="xs" fw={700} c={hasAvailability ? 'pink.9' : 'dimmed'}>
                {SHORT_DAYS[day]}
              </Text>
              <Text size="sm" fw={700} mt={4}>
                {formatWeeklyWindowSummary(windows)}
              </Text>
            </Paper>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}

export function formatWeeklyWindowSummary(windows: WeeklyAvailabilityWindow[]): string {
  return windows.length
    ? windows.map((window) => `${formatShortTime(window.startTime)}–${formatShortTime(window.endTime)}`).join(' · ')
    : '—';
}

function TimeOffSection({
  isEditing,
  form,
  editingOverride,
  services,
  serviceOptions,
  overrides,
  timezone,
  loadPastOverrides,
  updateOverride,
  saveOverrideDraft,
  setEditingOverride,
  setDraft,
  draft,
  removeOverride,
  appointmentConflicts,
  conflictsLoading,
  conflictAcknowledged,
  acknowledgeAppointmentConflicts,
}: {
  isEditing: boolean;
  form: OverrideForm;
  editingOverride?: OverrideForm;
  services: V2AvailabilityResources['healthcareServices'];
  serviceOptions: Array<{ value: string; label: string }>;
  overrides: AvailabilityOverrideDraft[];
  timezone: string;
  loadPastOverrides: () => Promise<AvailabilityOverrideDraft[]>;
  updateOverride: (update: Partial<OverrideForm>) => void;
  saveOverrideDraft: () => void;
  setEditingOverride: (value: OverrideForm | undefined) => void;
  setDraft: (draft: ReturnType<typeof useV2AvailabilityEditorState>['draft']) => void;
  draft: ReturnType<typeof useV2AvailabilityEditorState>['draft'];
  removeOverride: (items: AvailabilityOverrideDraft[]) => void;
  appointmentConflicts: import('./types').AppointmentConflict[];
  conflictsLoading: boolean;
  conflictAcknowledged: boolean;
  acknowledgeAppointmentConflicts: (acknowledged: boolean) => void;
}): JSX.Element {
  const visibleOverrides = isEditing ? overrides : filterCurrentOverrides(overrides, timezone);
  const currentGroups = useMemo(
    () => sortOverrideGroups(groupOverrides(visibleOverrides, services), 'ascending'),
    [services, visibleOverrides]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pastOverrides, setPastOverrides] = useState<AvailabilityOverrideDraft[]>([]);
  const [pastExpanded, setPastExpanded] = useState(false);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastLoaded, setPastLoaded] = useState(false);
  const [pastPage, setPastPage] = useState(1);

  const togglePastOverrides = (): void => {
    const nextExpanded = !pastExpanded;
    setPastExpanded(nextExpanded);
    if (!nextExpanded || pastLoaded || pastLoading) {
      return;
    }
    setPastLoading(true);
    loadPastOverrides()
      .then((loadedOverrides) => {
        setPastOverrides(loadedOverrides);
        setPastLoaded(true);
      })
      .catch((loadError: unknown) => {
        showNotification({
          color: 'red',
          message: loadError instanceof Error ? loadError.message : 'Could not load past time off.',
        });
      })
      .finally(() => setPastLoading(false));
  };

  const renderOverrideCards = (groups: OverrideGroup[]): JSX.Element[] =>
    groups.map((override) => (
      <Paper key={override.key} p="md" withBorder radius="md">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm">
            <ThemeIcon variant="transparent" color="pink">
              <IconCalendar size={20} />
            </ThemeIcon>
            <div>
              <Text fw={700}>{override.label}</Text>
              <Text size="sm" c={isEditing ? 'dimmed' : undefined}>
                {override.detail}
              </Text>
            </div>
          </Group>
          {isEditing && (
            <Group gap="xs">
              <Button variant="subtle" size="sm" onClick={() => setEditingOverride(overrideToForm(override.items))}>
                Edit
              </Button>
              <Button variant="subtle" size="sm" color="gray" onClick={() => removeOverride(override.items)}>
                Remove
              </Button>
            </Group>
          )}
        </Group>
      </Paper>
    ));

  const renderOverrideList = (groups: OverrideGroup[], page: number, setPage: (page: number) => void): JSX.Element => {
    const pageCount = Math.ceil(groups.length / OVERRIDES_PAGE_SIZE);
    const safePage = Math.min(page, Math.max(pageCount, 1));
    const pageGroups = groups.slice((safePage - 1) * OVERRIDES_PAGE_SIZE, safePage * OVERRIDES_PAGE_SIZE);
    return (
      <Stack gap="xs">
        <ScrollArea.Autosize mah={360} type="auto" offsetScrollbars>
          <Stack gap="sm">{renderOverrideCards(pageGroups)}</Stack>
        </ScrollArea.Autosize>
        {pageCount > 1 && <Pagination value={safePage} onChange={setPage} total={pageCount} size="sm" />}
      </Stack>
    );
  };

  return (
    <Stack gap="sm">
      <Text fw={700} size="lg">
        Time off
      </Text>
      {isEditing && (
        <>
          <Alert
            color="gray"
            icon={<IconInfoCircle size={17} />}
            styles={{ root: { background: 'var(--mantine-color-gray-0)' } }}
          >
            Block time without changing your weekly hours — a conference, a week of PTO, or a single morning you need
            back.
          </Alert>
          <Paper p="md" withBorder radius="md">
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
                <TextInput
                  label="Date"
                  type="date"
                  value={form.startDate}
                  onChange={(event) =>
                    updateOverride({
                      startDate: event.currentTarget.value,
                      endDate: form.endDate || event.currentTarget.value,
                    })
                  }
                  required
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <TextInput
                  label="Reason (optional)"
                  value={form.reason}
                  onChange={(event) => updateOverride({ reason: event.currentTarget.value })}
                  placeholder="e.g. PTO or conference"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
                <TextInput
                  label="Through"
                  type="date"
                  value={form.endDate}
                  onChange={(event) => updateOverride({ endDate: event.currentTarget.value })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                <Stack gap={4}>
                  <Text size="sm" fw={500}>
                    Hours
                  </Text>
                  <Group gap="xs" wrap="wrap">
                    <Switch
                      label="All day"
                      checked={form.allDay}
                      onChange={(event) =>
                        updateOverride({
                          allDay: event.currentTarget.checked,
                          ...(event.currentTarget.checked || form.startTime !== '00:00' || form.endTime !== '23:59'
                            ? {}
                            : { startTime: '09:00', endTime: '10:00' }),
                        })
                      }
                      color="pink"
                    />
                    {!form.allDay && (
                      <>
                        <TimeSelect
                          aria-label="Start time for time off"
                          value={form.startTime}
                          onChange={(value) => updateOverride({ startTime: value ?? '' })}
                        />
                        <Text c="dimmed">to</Text>
                        <TimeSelect
                          aria-label="End time for time off"
                          value={form.endTime}
                          onChange={(value) => updateOverride({ endTime: value ?? '' })}
                        />
                      </>
                    )}
                  </Group>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
                <MultiSelect
                  label="Applies to"
                  data={serviceOptions}
                  value={form.healthcareServiceIds}
                  onChange={(healthcareServiceIds) => updateOverride({ healthcareServiceIds })}
                  searchable
                  comboboxProps={{ withinPortal: false }}
                />
              </Grid.Col>
            </Grid>
            <Group mt="md" justify="space-between">
              <Button
                onClick={saveOverrideDraft}
                disabled={!form.startDate || !form.endDate || !form.healthcareServiceIds.length}
              >
                {editingOverride?.id ? 'Update' : 'Add'}
              </Button>
            </Group>
          </Paper>
          {appointmentConflicts.length > 0 && (
            <Alert color="orange" title="This block overlaps existing appointments">
              <Stack gap="xs">
                <Text size="sm">Appointments will not be changed. Confirm that you want to save this block:</Text>
                {appointmentConflicts.map((conflict) => (
                  <Text key={conflict.id} size="sm">
                    {new Date(conflict.start).toLocaleString()} · {conflict.patientDisplay ?? 'Patient'} ·{' '}
                    {conflict.status}
                  </Text>
                ))}
                <Checkbox
                  label="I acknowledge these appointments will remain unchanged."
                  checked={conflictAcknowledged}
                  onChange={(event) => acknowledgeAppointmentConflicts(event.currentTarget.checked)}
                  disabled={conflictsLoading}
                />
              </Stack>
            </Alert>
          )}
        </>
      )}
      {visibleOverrides.length === 0 ? (
        <Text c="dimmed">No time off configured.</Text>
      ) : (
        renderOverrideList(currentGroups, currentPage, setCurrentPage)
      )}
      {!isEditing && (
        <Stack gap="xs">
          <Button
            variant="subtle"
            color="gray"
            styles={{ inner: { justifyContent: 'flex-start' } }}
            leftSection={
              <IconChevronDown size={16} style={{ transform: pastExpanded ? 'rotate(180deg)' : undefined }} />
            }
            onClick={togglePastOverrides}
            aria-expanded={pastExpanded}
          >
            <Group gap="xs">
              <Text size="sm">Past time off</Text>
              {pastLoaded && (
                <Badge size="sm" color="gray" variant="light">
                  {sortOverrideGroups(groupOverrides(pastOverrides, services), 'descending').length}
                </Badge>
              )}
            </Group>
          </Button>
          {pastExpanded &&
            (pastLoading ? (
              <Loader size="sm" ml="xs" />
            ) : (
              renderOverrideList(
                sortOverrideGroups(groupOverrides(pastOverrides, services), 'descending'),
                pastPage,
                setPastPage
              )
            ))}
        </Stack>
      )}
    </Stack>
  );
}

export function filterCurrentOverrides(
  overrides: AvailabilityOverrideDraft[],
  timezone: string,
  today = getDateInTimezone(timezone)
): AvailabilityOverrideDraft[] {
  return overrides.filter((override) => override.date >= today);
}

function getDateInTimezone(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : new Date().toISOString().slice(0, 10);
}

interface OverrideGroup {
  key: string;
  label: string;
  detail: string;
  items: AvailabilityOverrideDraft[];
}

type OverrideSortDirection = 'ascending' | 'descending';

export function groupOverrides(
  overrides: AvailabilityOverrideDraft[],
  services: V2AvailabilityResources['healthcareServices']
): OverrideGroup[] {
  const sortedOverrides = [...overrides].sort(compareOverrideDates);
  const serviceIds = new Set(services.flatMap((service) => (service.id ? [service.id] : [])));
  const dailyEntries: Array<{ date: string; detail: string; items: AvailabilityOverrideDraft[] }> = [];

  for (let index = 0; index < sortedOverrides.length; ) {
    const date = sortedOverrides[index].date;
    const sameDate = sortedOverrides.filter((item) => item.date === date);
    const entriesByWindow = new Map<string, AvailabilityOverrideDraft[]>();
    for (const item of sameDate) {
      const key = `${item.startTime}|${item.endTime}|${item.reason ?? ''}|${item.status}`;
      const items = entriesByWindow.get(key) ?? [];
      items.push(item);
      entriesByWindow.set(key, items);
    }
    for (const items of entriesByWindow.values()) {
      const first = items[0];
      const itemServiceIds = new Set(items.map((item) => item.healthcareServiceId));
      const allAppointmentTypes =
        serviceIds.size > 0 &&
        itemServiceIds.size === serviceIds.size &&
        [...serviceIds].every((id) => itemServiceIds.has(id));
      const service = services.find((candidate) => candidate.id === first.healthcareServiceId);
      let serviceLabel = first.healthcareServiceId;
      if (allAppointmentTypes) {
        serviceLabel = 'All appointment types';
      } else if (service) {
        serviceLabel = getServiceCode(service).display || service.name || 'Appointment type';
      }
      dailyEntries.push({
        date,
        detail: `${formatShortTime(first.startTime)} – ${formatShortTime(first.endTime)} · ${serviceLabel}${first.reason ? ` · ${first.reason}` : ''} · Created by ${first.createdBy?.display ?? (first.createdBy?.reference ? first.createdBy.reference.replace('Practitioner/', '') : 'attribution unavailable')}`,
        items,
      });
    }
    index += sameDate.length;
  }

  return dailyEntries.reduce<OverrideGroup[]>((groups, entry) => {
    const previous = groups[groups.length - 1];
    if (
      previous &&
      previous.items[previous.items.length - 1].date === previousDate(entry.date) &&
      previous.detail === entry.detail
    ) {
      previous.items.push(...entry.items);
      previous.label = `${formatDate(previous.items[0].date)} – ${formatDate(entry.date)}`;
    } else {
      groups.push({
        key: `${entry.date}-${entry.items.map((item) => item.healthcareServiceId).join('-')}-${entry.items[0].startTime}`,
        label: formatDate(entry.date),
        detail: entry.detail,
        items: entry.items,
      });
    }
    return groups;
  }, []);
}

export function sortOverrideGroups(groups: OverrideGroup[], direction: OverrideSortDirection): OverrideGroup[] {
  return [...groups].sort((left, right) => {
    const comparison = left.items[0].date.localeCompare(right.items[0].date);
    return direction === 'ascending' ? comparison : -comparison;
  });
}

function compareOverrideDates(left: AvailabilityOverrideDraft, right: AvailabilityOverrideDraft): number {
  return left.date.localeCompare(right.date) || left.startTime.localeCompare(right.startTime);
}
function previousDate(date: string): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() - 1);
  return value.toISOString().slice(0, 10);
}
function formatDate(date: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}
function formatShortTime(value: string): string {
  const [hours, minutes] = value.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return value;
  }
  const suffix = hours >= 12 ? 'p' : 'a';
  const hour = hours % 12 || 12;
  return `${hour}${minutes === 0 ? '' : `:${String(minutes).padStart(2, '0')}`}${suffix}`;
}
function timezoneLabel(timezone: string): string {
  if (!timezone) {
    return 'Timezone not configured';
  }
  const city = timezone.split('/').pop()?.replaceAll('_', ' ') ?? timezone;
  const zone = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value;
  return `${city} (${zone ?? timezone})`;
}
function weeklyServiceLabel(
  id: string,
  ids: string[] | undefined,
  services: V2AvailabilityResources['healthcareServices']
): string {
  if (ids && ids.length === services.length) {
    return 'All appointment types';
  }
  const service = services.find((candidate) => candidate.id === id);
  return service ? getServiceCode(service).display || service.name || 'Appointment type' : 'All appointment types';
}
function formatBufferEndTime(hour: number, bufferMinutes: number): string {
  const totalMinutes = hour * 60 + 30 + bufferMinutes;
  const endHour = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${endHour % 12 || 12}:${String(minutes).padStart(2, '0')} ${endHour >= 12 ? 'PM' : 'AM'}`;
}
function timeOptions(): Array<{ value: string; label: string }> {
  return Array.from({ length: 48 }, (_, index) => {
    const minutes = index * 30;
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    return { value, label: `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}` };
  });
}

export interface OverrideForm {
  id?: string;
  existingItems: AvailabilityOverrideDraft[];
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  healthcareServiceIds: string[];
  status: AvailabilityOverrideDraft['status'];
  reason: string;
  allDay: boolean;
}
function emptyOverrideForm(healthcareServiceId = ''): OverrideForm {
  return {
    existingItems: [],
    healthcareServiceIds: healthcareServiceId ? [healthcareServiceId] : [],
    startDate: '',
    endDate: '',
    startTime: '09:00',
    endTime: '10:00',
    status: 'busy-unavailable',
    reason: '',
    allDay: false,
  };
}
function overrideToForm(items: AvailabilityOverrideDraft[]): OverrideForm {
  const override = items[0];
  if (!override) {
    return emptyOverrideForm();
  }
  return {
    id: override.id,
    existingItems: items,
    startDate: override.date,
    endDate: override.date,
    startTime: override.startTime,
    endTime: override.endTime,
    healthcareServiceIds: [...new Set(items.map((item) => item.healthcareServiceId))],
    status: 'busy-unavailable',
    reason: override.reason ?? '',
    allDay: override.startTime === '00:00' && override.endTime === '23:59',
  };
}

export function buildOverrideDrafts(form: OverrideForm): AvailabilityOverrideDraft[] {
  const existingByDateAndService = new Map(
    form.existingItems.map((item) => [`${item.date}|${item.healthcareServiceId}`, item])
  );
  let fallbackId = form.existingItems.length === 1 ? form.existingItems[0]?.id : undefined;
  return getDatesInRange(form.startDate, form.endDate).flatMap((date) =>
    form.healthcareServiceIds.map((healthcareServiceId) => {
      const existing = existingByDateAndService.get(`${date}|${healthcareServiceId}`);
      const id = existing?.id ?? fallbackId;
      fallbackId = undefined;
      return {
        id,
        healthcareServiceId,
        date,
        startTime: form.allDay ? '00:00' : form.startTime,
        endTime: form.allDay ? '23:59' : form.endTime,
        reason: form.reason || undefined,
        status: form.status,
      };
    })
  );
}
function getDatesInRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const current = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  while (current <= end) {
    dates.push(current.toISOString().slice(0, 10));
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return dates;
}
